import { ArrowLeftOutlined, CloseOutlined, DownloadOutlined, SaveOutlined, SyncOutlined } from "@ant-design/icons"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Button, Card, Checkbox, Col, DatePicker, Divider, Form, Input, message, Row, Select, Skeleton, Space, Table, Typography } from "antd"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { ContainerFull } from "../../components/GeneralComponents"
import { getInvoices, updateInvoice, updateInvoiceOrders } from "../../endpoints/invoiceEndpoints"

const { Title, Text } = Typography

export const EditInvoice = (): JSX.Element => {
    const { invoiceId } = useParams<{ invoiceId: string }>()
    const navigate = useNavigate()
    const [useOfficeAddress, setUseOfficeAddress] = useState(false)
    const [form] = Form.useForm()
    const queryClient = useQueryClient()

    const { isPending, isError, data, error } = useQuery({
        queryKey: ["getInvoices"],
        queryFn: getInvoices,
        refetchOnMount: "always",
    })

    const invoice = data?.invoices?.find((inv) => inv.id === parseInt(invoiceId || "0"))

    useEffect(() => {
        if (invoice) {
            setUseOfficeAddress(invoice.plabAddress === "briden")

            form.setFieldsValue({
                billingInstitution: invoice.billingInstitution,
                billingContactName: invoice.billingContactName,
                billingEmail: invoice.billingEmail,
                billingAddress: [
                    invoice.billingAddressLine1,
                    invoice.billingAddressLine2,
                    `${invoice.billingCity} ${invoice.billingState} ${invoice.billingZipcode}`,
                    invoice.billingCountry,
                ].filter(Boolean).join("\n"),
                paymentMethod: invoice.paymentMethod,
                status: invoice.status,
                billingDate: invoice.billingDate ? dayjs(invoice.billingDate) : null,
                dueDate: invoice.dueDate ? dayjs(invoice.dueDate) : null,
                discount: invoice.discount,
                totalAmount: invoice.totalAmount,
                payment: invoice.payment,
                notes: invoice.notes,
            })
        }
    }, [invoice, form])

    if (isPending) {
        return <Skeleton />
    }

    if (isError) {
        return (
            <ContainerFull>
                <Card title="Error">
                    <p>Error loading invoice: {error?.message}</p>
                    <Button onClick={() => navigate("/admin/invoices")}>
                        <ArrowLeftOutlined /> Back to Invoices
                    </Button>
                </Card>
            </ContainerFull>
        )
    }

    if (!invoice) {
        return (
            <ContainerFull>
                <Card title="Invoice Not Found">
                    <p>Invoice #{invoiceId} was not found.</p>
                    <Button onClick={() => navigate("/admin/invoices")}>
                        <ArrowLeftOutlined /> Back to Invoices
                    </Button>
                </Card>
            </ContainerFull>
        )
    }

    const handleDownloadInvoice = async () => {
        try {
            const url = `/api/download/invoice/${invoice.id}?r=${Math.random()}`
            window.open(url, "_blank")
        } catch (downloadError) {
            console.error("Error downloading invoice:", downloadError)
            message.error("Failed to download invoice")
        }
    }

    const handleUpdateInvoice = async (values: any) => {
        try {
            message.loading({ content: "Updating invoice, orders, and regenerating PDF...", key: "update" })

            const response = await updateInvoice({
                invoiceId: invoice.id,
                billingInstitution: values.billingInstitution,
                billingContactName: values.billingContactName,
                billingEmail: values.billingEmail,
                billingAddress: values.billingAddress,
                paymentMethod: values.paymentMethod,
                status: values.status,
                billingDate: values.billingDate ? values.billingDate.toISOString() : undefined,
                dueDate: values.dueDate ? values.dueDate.toISOString() : undefined,
                plabAddress: useOfficeAddress ? "briden" : "brentwood",
                discount: values.discount,
                totalAmount: values.totalAmount,
                payment: values.payment,
                notes: values.notes,
            })

            if (response.code === 1000) {
                message.success({ content: "Invoice and orders updated, PDF regenerated successfully", key: "update" })
                queryClient.invalidateQueries({ queryKey: ["getInvoices"] })
            } else {
                message.error({ content: response.message || "Failed to update invoice", key: "update" })
            }
        } catch (updateError) {
            console.error("Error updating invoice:", updateError)
            message.error({ content: "Failed to update invoice", key: "update" })
        }
    }

    const handleUpdateOrders = async () => {
        try {
            message.loading({ content: "Refreshing invoice orders and recalculating totals...", key: "updateOrders" })

            const response = await updateInvoiceOrders({ invoiceId: invoice.id })

            if (response.code === 1000) {
                message.success({ content: "Invoice orders updated successfully", key: "updateOrders" })
                queryClient.invalidateQueries({ queryKey: ["getInvoices"] })
            } else {
                message.error({ content: response.message || "Failed to update invoice orders", key: "updateOrders" })
            }
        } catch (updateOrdersError) {
            console.error("Error updating invoice orders:", updateOrdersError)
            message.error({ content: "Failed to update invoice orders", key: "updateOrders" })
        }
    }

    const ordersData = invoice.invoiceDetails && Array.isArray(invoice.invoiceDetails)
        ? invoice.invoiceDetails.map((order: any, index: number) => {
            const billingType = order.billing_type || "normal"
            const isFreeRerun = billingType === "free rerun"

            return {
                key: index,
                date: order.date,
                orderNumber: order.order_number,
                productName: order.product_name,
                contact: order.order_contact,
                samples: order.nsamples,
                notes: isFreeRerun ? "free rerun" : "",
                unitPrice: isFreeRerun ? 0 : order.unit_price,
                total: isFreeRerun ? 0 : order.order_list_price,
            }
        })
        : []

    const totalSamples = ordersData.reduce((sum, order) => sum + (Number(order.samples) || 0), 0)

    const freeRerunSamples = ordersData
        .filter((order) => order.notes === "free rerun")
        .reduce((sum, order) => sum + (Number(order.samples) || 0), 0)

    const samplesByProduct: Record<string, number> = {}
    for (const order of ordersData) {
        if (order.notes !== "free rerun") {
            const product = order.productName || "Unknown"
            samplesByProduct[product] = (samplesByProduct[product] || 0) + (Number(order.samples) || 0)
        }
    }

    const orderColumns = [
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
        },
        {
            title: "Order #",
            dataIndex: "orderNumber",
            key: "orderNumber",
            render: (orderNumber: number) => (
                <Link to={`/admin/order/${orderNumber}`} target="_blank">
                    #{orderNumber}
                </Link>
            ),
        },
        {
            title: "Product",
            dataIndex: "productName",
            key: "productName",
        },
        {
            title: "Contact",
            dataIndex: "contact",
            key: "contact",
        },
        {
            title: "Samples",
            dataIndex: "samples",
            key: "samples",
            align: "center" as const,
        },
        {
            title: "Unit Price",
            dataIndex: "unitPrice",
            key: "unitPrice",
            align: "right" as const,
            render: (price: number) => `$${(price || 0).toFixed(2)}`,
        },
        {
            title: "Total",
            dataIndex: "total",
            key: "total",
            align: "right" as const,
            render: (total: number) => `$${(total || 0).toFixed(2)}`,
        },
        {
            title: "Notes",
            dataIndex: "notes",
            key: "notes",
            render: (notes: string) => notes || "-",
        },
    ]

    return (
        
            <div style={{ width: "100%", maxWidth: 1680, margin: "0 auto", paddingRight: 24 }}>
                <Card
                    style={{
                        marginBottom: 24,
                        background: "#f6f6f2",
                        border: "none",
                        borderRadius: 12,
                    }}
                    styles={{ body: { padding: "24px 32px" } }}
                >
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Space size="large" align="center">
                                <Button
                                    onClick={() => navigate("/admin/invoices")}
                                    type="text"
                                    icon={<ArrowLeftOutlined />}
                                    style={{
                                        color: "black",
                                        border: "1px solid rgba(100,100,100,0.3)",
                                        borderRadius: 8,
                                    }}
                                    size="large"
                                >
                                    Back to Invoices
                                </Button>
                                <Title level={2} style={{ margin: 0, color: "black" }}>
                                    Edit Invoice #{invoice.id}
                                </Title>
                            </Space>
                        </Col>
                        <Col>
                            <Button
                                icon={<DownloadOutlined />}
                                onClick={handleDownloadInvoice}
                                style={{
                                    background: "rgba(255,255,255,0.2)",
                                    border: "1px solid rgba(100,100,100,0.3)",
                                    color: "black",
                                    borderRadius: 8,
                                }}
                                size="large"
                            >
                                Download PDF
                            </Button>
                        </Col>
                    </Row>
                </Card>

                <Form form={form} layout="vertical" onFinish={handleUpdateInvoice} size="large">
                    <Row gutter={[24, 24]}>
                        <Col xs={24} lg={10}>
                            <Card
                                title={
                                    <Space>
                                        <Title level={4} style={{ margin: 0, color: "#1890ff" }}>
                                            Invoice Details
                                        </Title>
                                    </Space>
                                }
                                style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                                styles={{ body: { padding: "32px" } }}
                            >
                                <Row gutter={24}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="billingDate"
                                            label={<Text strong>Invoice Date</Text>}
                                            rules={[{ required: true, message: "Please select invoice date" }]}
                                        >
                                            <DatePicker style={{ width: "100%" }} placeholder="Select invoice date" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="dueDate"
                                            label={<Text strong>Due Date</Text>}
                                            rules={[{ required: true, message: "Please select due date" }]}
                                        >
                                            <DatePicker style={{ width: "100%" }} placeholder="Select due date" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Divider style={{ margin: "24px 0" }} />

                                <Title level={5} style={{ marginBottom: 16, color: "#595959" }}>
                                    Billing Information
                                </Title>
                                <Row gutter={24}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="billingInstitution"
                                            label={<Text strong>Institution</Text>}
                                            rules={[{ required: true, message: "Please enter institution" }]}
                                        >
                                            <Input placeholder="Enter institution name" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="billingContactName"
                                            label={<Text strong>Contact Name</Text>}
                                            rules={[{ required: true, message: "Please enter contact name" }]}
                                        >
                                            <Input placeholder="Enter contact name" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={24}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="billingEmail"
                                            label={<Text strong>Email</Text>}
                                            rules={[
                                                { required: true, message: "Please enter email" },
                                                { type: "email", message: "Please enter a valid email" },
                                            ]}
                                        >
                                            <Input placeholder="Enter email address" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item name="paymentMethod" label={<Text strong>Payment Method</Text>}>
                                            <Input placeholder="Enter payment method" />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row gutter={24}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="billingAddress"
                                            label={<Text strong>Billing Address</Text>}
                                            rules={[{ required: true, message: "Please enter billing address" }]}
                                        >
                                            <Input.TextArea rows={4} placeholder="Enter billing address" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12}>
                                        <Form.Item
                                            name="status"
                                            label={<Text strong>Invoice Status</Text>}
                                            rules={[{ required: true, message: "Please select status" }]}
                                        >
                                            <Select placeholder="Select status">
                                                <Select.Option value="due">Due</Select.Option>
                                                <Select.Option value="paid">Paid</Select.Option>
                                                <Select.Option value="canceled">Canceled</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Divider style={{ margin: "24px 0" }} />

                                <Card
                                    size="small"
                                    style={{
                                        background: "#f6ffed",
                                        border: "1px solid #b7eb8f",
                                        borderRadius: 8,
                                    }}
                                >
                                    <Checkbox
                                        checked={useOfficeAddress}
                                        onChange={(event) => setUseOfficeAddress(event.target.checked)}
                                        style={{ fontSize: "14px" }}
                                    >
                                        <Text strong>Use office address for PDF generation</Text>
                                    </Checkbox>
                                    <br />
                                    <Text type="secondary" style={{ fontSize: "12px", marginLeft: 24 }}>
                                        When checked, the office address will be used instead of billing address
                                    </Text>
                                </Card>

                                <Row justify="start" style={{ marginTop: 32 }}>
                                    <Space size="middle">
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            size="large"
                                            icon={<SaveOutlined />}
                                            style={{
                                                borderRadius: 8,
                                                boxShadow: "0 2px 8px rgba(24, 144, 255, 0.3)",
                                            }}
                                        >
                                            Save Changes
                                        </Button>
                                        <Button
                                            size="large"
                                            onClick={() => navigate("/admin/invoices")}
                                            icon={<CloseOutlined />}
                                            style={{ borderRadius: 8 }}
                                        >
                                            Cancel
                                        </Button>
                                    </Space>
                                </Row>
                            </Card>
                        </Col>

                        <Col xs={24} lg={14}>
                            <Space direction="vertical" size="large" style={{ width: "100%" }}>
                                <Card
                                    title={
                                        <Title level={4} style={{ margin: 0, color: "#1890ff" }}>
                                            Orders in This Invoice
                                        </Title>
                                    }
                                    extra={
                                        <Button icon={<SyncOutlined />} onClick={handleUpdateOrders}>
                                            Update Orders
                                        </Button>
                                    }
                                    style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                                    styles={{ body: { padding: "24px" } }}
                                >
                                    {ordersData.length > 0 ? (
                                        <>
                                            <Table
                                                columns={orderColumns}
                                                dataSource={ordersData}
                                                pagination={false}
                                                size="small"
                                                style={{ background: "#fafafa", borderRadius: 8 }}
                                            />
                                            <Text strong style={{ display: "block", marginTop: 12 }}>
                                                Total Samples: {totalSamples}
                                            </Text>
                                            <div style={{ marginTop: 8, paddingLeft: 12, borderLeft: "3px solid #e8e8e8" }}>
                                                {Object.entries(samplesByProduct).map(([product, count]) => (
                                                    <Text key={product} type="secondary" style={{ display: "block", fontSize: 13 }}>
                                                        {product}: {count}
                                                    </Text>
                                                ))}
                                                {freeRerunSamples > 0 && (
                                                    <Text type="secondary" style={{ display: "block", fontSize: 13 }}>
                                                        Free Rerun: {freeRerunSamples}
                                                    </Text>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <div
                                            style={{
                                                textAlign: "center",
                                                padding: "40px 20px",
                                                background: "#fafafa",
                                                borderRadius: 8,
                                                border: "2px dashed #d9d9d9",
                                            }}
                                        >
                                            <Text type="secondary" style={{ fontSize: "16px" }}>
                                                No order details available
                                            </Text>
                                        </div>
                                    )}
                                </Card>

                                <Card
                                    title={
                                        <Title level={4} style={{ margin: 0, color: "#1890ff" }}>
                                            Invoice Notes
                                        </Title>
                                    }
                                    style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                                    styles={{ body: { padding: "24px" } }}
                                >
                                    <Form.Item name="notes" style={{ marginBottom: 0 }}>
                                        <Input.TextArea rows={6} placeholder="Add notes for this invoice" />
                                    </Form.Item>
                                </Card>

                                <Card
                                    title={
                                        <Title level={4} style={{ margin: 0, color: "#1890ff" }}>
                                            Invoice Summary
                                        </Title>
                                    }
                                    style={{ borderRadius: 12, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                                    styles={{ body: { padding: "24px" } }}
                                >
                                    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                                        <Row justify="space-between" align="middle">
                                            <Col>
                                                <Text style={{ fontSize: "16px" }}>Subtotal:</Text>
                                            </Col>
                                            <Col>
                                                <Text style={{ fontSize: "16px", fontWeight: 500 }}>
                                                    ${(invoice.subTotal || 0).toFixed(2)}
                                                </Text>
                                            </Col>
                                        </Row>

                                        <Row justify="space-between" align="middle">
                                            <Col>
                                                <Text style={{ fontSize: "16px" }}>Discount:</Text>
                                            </Col>
                                            <Col>
                                                <Form.Item
                                                    name="discount"
                                                    style={{ margin: 0, width: "140px" }}
                                                    rules={[
                                                        {
                                                            validator: (_, value) => {
                                                                const numericValue = parseFloat(value)
                                                                if (value === undefined || value === "" || value === null) {
                                                                    return Promise.resolve()
                                                                }
                                                                if (isNaN(numericValue) || numericValue < 0) {
                                                                    return Promise.reject(new Error("Discount must be positive"))
                                                                }
                                                                return Promise.resolve()
                                                            },
                                                        },
                                                    ]}
                                                >
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        placeholder="0.00"
                                                        style={{ textAlign: "right" }}
                                                        addonBefore="$"
                                                        size="middle"
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        <Divider style={{ margin: "16px 0" }} />

                                        <Row justify="space-between" align="middle">
                                            <Col>
                                                <Text style={{ fontSize: "18px", fontWeight: "bold" }}>Total:</Text>
                                            </Col>
                                            <Col>
                                                <Form.Item
                                                    name="totalAmount"
                                                    style={{ margin: 0, width: "140px" }}
                                                    rules={[
                                                        {
                                                            validator: (_, value) => {
                                                                const numericValue = parseFloat(value)
                                                                if (value === undefined || value === "" || value === null) {
                                                                    return Promise.reject(new Error("Total amount is required"))
                                                                }
                                                                if (isNaN(numericValue) || numericValue < 0) {
                                                                    return Promise.reject(new Error("Total must be positive"))
                                                                }
                                                                return Promise.resolve()
                                                            },
                                                        },
                                                    ]}
                                                >
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        placeholder="0.00"
                                                        style={{ textAlign: "right", fontWeight: "bold" }}
                                                        addonBefore="$"
                                                        size="middle"
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>

                                        <Row justify="space-between" align="middle" style={{ marginTop: "12px" }}>
                                            <Col>
                                                <Text style={{ fontSize: "16px" }}>Payment Received:</Text>
                                            </Col>
                                            <Col>
                                                <Form.Item
                                                    name="payment"
                                                    style={{ margin: 0, width: "140px" }}
                                                    rules={[
                                                        {
                                                            validator: (_, value) => {
                                                                const numericValue = parseFloat(value)
                                                                if (value === undefined || value === "" || value === null) {
                                                                    return Promise.resolve()
                                                                }
                                                                if (isNaN(numericValue) || numericValue < 0) {
                                                                    return Promise.reject(new Error("Payment received must be positive"))
                                                                }
                                                                return Promise.resolve()
                                                            },
                                                        },
                                                    ]}
                                                >
                                                    <Input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        placeholder="0.00"
                                                        style={{ textAlign: "right" }}
                                                        addonBefore="$"
                                                        size="middle"
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Space>
                                </Card>
                            </Space>
                        </Col>
                    </Row>
                </Form>
            </div>
        
    )
}
